import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string, setFreshLogin?: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    console.log('AuthContext: Initializing with stored data');
    console.log('AuthContext: Stored token exists:', !!storedToken);
    console.log('AuthContext: Stored user exists:', !!storedUser);

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      console.log('AuthContext: User authenticated from storage');
    } else {
      console.log('AuthContext: No stored authentication data');
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('AuthContext: Starting login process');
      const response: AuthResponse = await authAPI.login(email, password);
      console.log('AuthContext: Login response received:', response);
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      sessionStorage.setItem('freshLogin', 'true'); // Flag to indicate fresh login
      console.log('AuthContext: State updated, user:', response.user);
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      throw error;
    }
  };

  const loginWithToken = async (authToken: string, setFreshLogin: boolean = false) => {
    try {
      console.log('AuthContext: Starting WorkOS token login');
      
      // Store the token first
      setToken(authToken);
      localStorage.setItem('token', authToken);
      
      // Fetch user profile using the token
      const response = await authAPI.getMe();
      
      if (response.success && response.data) {
        const user = response.data;
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
        // Only set freshLogin flag if explicitly requested (from callback)
        if (setFreshLogin) {
          sessionStorage.setItem('freshLogin', 'true');
        }
        console.log('AuthContext: WorkOS login successful, user:', user);
      } else {
        throw new Error('Failed to get user profile');
      }
    } catch (error) {
      console.error('AuthContext: WorkOS login error:', error);
      // Clear stored token on error
      setToken(null);
      localStorage.removeItem('token');
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response: AuthResponse = await authAPI.register(name, email, password);
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      sessionStorage.setItem('freshLogin', 'true'); // Flag to indicate fresh login
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('freshLogin'); // Clear fresh login flag
  };

  const value = {
    user,
    token,
    login,
    loginWithToken,
    register,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
